import React, { useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async"; // For meta tags

const LeadershipImpact = () => {
  const [isHovered, setIsHovered] = useState(false);

  const impacts = [
    "Education for All Initiative — Building modern schools in underserved communities to provide access to quality education for children across Ghana.",
    "Community Health Projects — Supporting hospitals and clinics to improve healthcare access and wellbeing.",
    "Philanthropy & Donations — Regular donations to orphanages, churches, and community programs uplifting lives and fostering growth.",
    "Youth Empowerment — Providing skills training and startup support for young entrepreneurs.",
    "Sustainable Development — Investing in eco-friendly infrastructure and clean water initiatives.",
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Kwame O. Sarfo",
    jobTitle: "Entrepreneur & Philanthropist",
    description:
      "Kwame O. Sarfo leads impactful initiatives in education, community health, philanthropy, and sustainable development through Solonet Properties, Ice Chest Mineral Water, and Global Timber.",
    sameAs: [
      "https://www.solonetproperties.com",
      "https://www.linkedin.com/in/kwameosarfo",
      "https://www.instagram.com/kwameosarfo",
    ],
    knowsAbout: [
      "Leadership",
      "Philanthropy",
      "Real Estate",
      "Sustainable Development",
      "Community Empowerment",
    ],
  };

  return (
    <section
      id="impact"
      className="relative bg-[#0a0a0a] text-white py-24 px-6 md:px-20 overflow-hidden"
    >
      {/* SEO Metadata */}
      <Helmet>
        <title>Leadership & Impact | Kwame O. Sarfo</title>
        <meta
          name="description"
          content="Explore Kwame O. Sarfo’s leadership and impact through education, community health, philanthropy, youth empowerment, and sustainable development initiatives in Ghana."
        />
        <meta
          name="keywords"
          content="Kwame O. Sarfo, leadership, impact, philanthropy, Ghana, education, Solonet Properties, Ice Chest Mineral Water, Global Timber, community development"
        />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Header */}
      <div className="max-w-4xl mx-auto text-center mb-16">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="text-4xl md:text-5xl font-playfair text-[#d4af37]"
        >
          Leadership & Impact
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-gray-400 mt-4 max-w-2xl mx-auto leading-relaxed"
        >
          Discover how <strong>Kwame O. Sarfo</strong> is transforming lives
          through education, healthcare, and sustainable community development.
          His leadership reflects a legacy of compassion and empowerment across
          Ghana and beyond.
        </motion.p>
      </div>

      {/* Circular Orbit Animation */}
      <div
        className="relative h-[600px] flex items-center justify-center"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Rotating Container */}
        <motion.div
          animate={{ ["--rot"]: isHovered ? "0deg" : "360deg" }}
          transition={{
            ["--rot"]: { duration: 60, repeat: Infinity, ease: "linear" },
          }}
          style={{ ["--rot"]: "0deg" }}
          className="absolute w-[500px] h-[500px] rounded-full border border-[#d4af37]/50 shadow-[0_0_40px_rgba(212,175,55,0.25)] flex items-center justify-center"
        >
          {/* Impact Cards */}
          <div className="absolute inset-0 pointer-events-none">
            {impacts.map((impact, index) => {
              const angle = (index / impacts.length) * 2 * Math.PI;
              const radius = 210;
              const x = Math.cos(angle) * radius;
              const y = Math.sin(angle) * radius;

              return (
                <article
                  key={index}
                  className="absolute flex justify-center items-center pointer-events-auto"
                  style={{
                    top: `calc(50% + ${y}px)`,
                    left: `calc(50% + ${x}px)`,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.18, duration: 0.9 }}
                    className="w-56 bg-[#111]/90 border border-[#d4af37]/30 rounded-2xl p-5 text-center shadow-[0_0_20px_rgba(212,175,55,0.2)] backdrop-blur-md hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all duration-300"
                    style={{
                      transform: "rotate(calc(0deg - var(--rot)))",
                      transformOrigin: "center center",
                    }}
                  >
                    <h3 className="text-[#d4af37] font-semibold mb-2 text-sm uppercase tracking-wide">
                      {impact.split(" — ")[0]}
                    </h3>
                    <p className="text-gray-300 text-xs leading-relaxed">
                      {impact.split(" — ")[1]}
                    </p>
                  </motion.div>
                </article>
              );
            })}
          </div>
        </motion.div>

        {/* Center Core */}
        <div className="absolute text-center text-[#d4af37] font-playfair text-xl bg-[#111]/90 px-8 py-4 rounded-full border border-[#d4af37]/50 shadow-[0_0_25px_rgba(212,175,55,0.25)] backdrop-blur-sm">
          Leadership Impact
        </div>
      </div>
    </section>
  );
};

export default LeadershipImpact;
